<!--
  DesignReadinessStrip.vue -- what needs the operator, above the sizes.

  Card: Decoration color + family model (https://trello.com/c/2hcgDM7J)
  Handoff: planning/design-handoffs/handoff_2026-08-23/
           2026-08-23-decoration-family-model/
  Locked Decision 10.

  The strip LEADS with what needs the operator -- changes requested,
  awaiting a client, without a production file -- and only then the
  plain totals. **Approved counts are never the lead.**

  That ordering is the whole point of the component, so it is
  structural rather than stylistic: the work items and the totals are
  separate DOM regions in that order, and `readinessItems` (in
  utils/decorationFamilySelectors) has no bucket for approved at all.
  A future edit that wanted to lead with approval would have to add a
  bucket AND reorder the template, and a test fails on either.

  The counts come from `payload.rollup`, which the read model documents
  as COUNTS OVER SIZES and never a design-level verdict. A count cannot
  be mistaken for "this design is approved" the way a status string
  can -- which is the failure the shipped launch gate had, clearing a
  design whose back had no stitch file.
-->
<template>
    <div class="drs" data-testid="design-readiness-strip">
        <!-- The lead: what needs the operator, worst first. -->
        <div class="drs__work">
            <span
                v-if="!items.length"
                class="drs__clear"
                data-testid="readiness-clear"
            >Nothing needs you on this design.</span>

            <span
                v-for="item in items"
                v-else
                :key="item.kind"
                class="drs__item"
                :class="`drs__item--${item.tone}`"
                :data-testid="`readiness-item-${item.kind}`"
            >{{ item.label }}</span>
        </div>

        <!-- Then, and only then, the plain totals. -->
        <div class="drs__totals" data-testid="readiness-totals">
            {{ totalsText }}
        </div>
    </div>
</template>

<script>
import { readinessItems } from "@/utils/decorationFamilySelectors";

function plural(n, word) {
    return `${n} ${word}${n === 1 ? "" : "s"}`;
}

export default {
    name: "DesignReadinessStrip",
    props: {
        /** The whole family payload. */
        family: {
            type: Object,
            required: true,
        },
    },
    computed: {
        items() {
            return readinessItems(this.family);
        },
        totalsText() {
            const r = this.family?.rollup ?? {};
            const parts = [
                plural(Number(r.sizes ?? 0), "size"),
                plural(Number(r.methods ?? 0), "method"),
                // "0 clients" reads as a deficiency; a design with no
                // client is not one (Locked Decision 16).
                Number(r.clients ?? 0) === 0
                    ? "No client yet"
                    : plural(Number(r.clients), "client"),
                plural(Number(r.placements ?? 0), "placement"),
            ];
            return parts.join(" · ");
        },
    },
};
</script>

<style scoped>
.drs {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
    margin-bottom: 16px;
    padding: 11px 14px;
    background: var(--tf-surface);
    border: 1px solid var(--tf-border);
    border-radius: var(--tf-radius-md);
}
.drs__work {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    flex: 1;
    min-width: 0;
}
.drs__item {
    display: inline-flex;
    align-items: center;
    border-radius: var(--tf-radius-pill);
    padding: 4px 12px;
    font-size: 12.5px;
    font-weight: 500;
}
.drs__item--changes {
    background: var(--tf-chip-error-bg);
    color: var(--tf-chip-error-fg);
}
.drs__item--awaiting {
    background: var(--tf-chip-pending-bg);
    color: var(--tf-chip-pending-fg);
}
.drs__item--production {
    background: var(--tf-chip-info-bg);
    color: var(--tf-chip-info-fg);
}
.drs__clear {
    font-size: 13px;
    color: var(--tf-fg-3);
}
.drs__totals {
    font-size: 12.5px;
    color: var(--tf-fg-4);
    flex-shrink: 0;
}
</style>
